import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { FormSelectField } from '../../../../design-system/form-select-field/form-select-field';
import { FormTextField } from '../../../../design-system/form-text-field/form-text-field';
import { Form } from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { getUniqueLevelCategories } from '../../../../utils/get-unique-level-categories';

const createFormSchema = (uniqueLevel2Categories: string[]) =>
  z.object({
    level1Category: z.string({ required_error: 'Category is required' }),
    level2Category: z
      .string({ required_error: 'Subcategory is required' })
      .trim()
      .min(1, { message: 'Subcategory is required' })
      .refine((val) => !uniqueLevel2Categories.includes(val), {
        message: 'Subcategory name already exists',
      }),
  });

export type CreateCategoryFormFormValues = z.infer<ReturnType<typeof createFormSchema>>;

type CreateCategoryFormProps = {
  level1Category?: string;
  categories: CategoryView[];
  isLoading: boolean;
  onSubmit: (values: CreateCategoryFormFormValues) => void;
  onCancel: () => void;
};

export const CreateCategoryForm = ({
  level1Category,
  categories,
  isLoading,
  onSubmit,
  onCancel,
}: CreateCategoryFormProps) => {
  const uniqueLevel2Categories = getUniqueLevelCategories({ categories, level: 'level2' });

  const form = useForm<CreateCategoryFormFormValues>({
    resolver: zodResolver(createFormSchema(uniqueLevel2Categories)),
    defaultValues: {
      level1Category,
    },
    mode: 'onTouched',
  });

  const uniqueLevel1Categories = getUniqueLevelCategories({ categories, level: 'level1' });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => onSubmit(values))}>
        <Stack space={4} align="stretch">
          <FormSelectField
            label="Category"
            inputName="level1Category"
            placeholder="Select a category"
            children={uniqueLevel1Categories.map((category) => (
              <Select.Item key={category} value={category}>
                {category}
              </Select.Item>
            ))}
          />

          <FormTextField label="Subcategory" inputName="level2Category" placeholder="Enter new subcategory name" />

          <Inline>
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={!form.formState.isValid}
              isLoading={form.formState.isLoading || isLoading}
              className="flex-1"
            >
              Save
            </Button>

            <Button variant="secondary" size="lg" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Form>
  );
};
